import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  User, Hospital, Calendar, Heartbeat, FileText,
  ClockCounterClockwise, Copy, CheckCircle, Warning, XCircle,
  CaretDown, CaretRight, Link
} from "@phosphor-icons/react";
import AdjudicationPanel from "@/components/AdjudicationPanel";

const CLASS_LABELS = {
  discharge_summary: "Discharge Summary",
  hospital_bill: "Hospital Bill",
  hospital_bill_summary: "Summary Bill",
  hospital_bill_detailed: "Detailed Bill",
  pharmacy_bill: "Pharmacy Bill",
  lab_report: "Lab Report",
  prescription: "Prescription",
  id_proof: "ID Proof",
  cancelled_cheque: "Cancelled Cheque",
  insurance_card: "Insurance Card",
  claim_form: "Claim Form",
  consent_form: "Consent Form",
  investigation_report: "Investigation Report",
  pre_hospitalization_bill: "Pre-Hospitalization Bill",
  post_hospitalization_bill: "Post-Hospitalization Bill",
  admission_notes: "Admission Notes",
  operative_notes: "Operative Notes",
  other: "Other"
};

const CLASS_COLORS = {
  discharge_summary: "bg-purple-50 text-purple-700 border-purple-200",
  hospital_bill: "bg-blue-50 text-blue-700 border-blue-200",
  hospital_bill_summary: "bg-sky-50 text-sky-700 border-sky-200",
  hospital_bill_detailed: "bg-blue-50 text-blue-700 border-blue-200",
  pharmacy_bill: "bg-cyan-50 text-cyan-700 border-cyan-200",
  lab_report: "bg-green-50 text-green-700 border-green-200",
  prescription: "bg-yellow-50 text-yellow-700 border-yellow-200",
  id_proof: "bg-orange-50 text-orange-700 border-orange-200",
  cancelled_cheque: "bg-pink-50 text-pink-700 border-pink-200",
  investigation_report: "bg-indigo-50 text-indigo-700 border-indigo-200",
  pre_hospitalization_bill: "bg-violet-50 text-violet-700 border-violet-200",
  post_hospitalization_bill: "bg-teal-50 text-teal-700 border-teal-200",
  consent_form: "bg-slate-50 text-slate-700 border-slate-200",
  other: "bg-gray-50 text-gray-600 border-gray-200"
};

function KVItem({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <dt className="label-xs mb-1">{label}</dt>
      <dd className="text-sm font-medium text-gray-900" data-testid={`kv-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {value}
      </dd>
    </div>
  );
}

function DocGroup({ classification, pages, onPageClick }) {
  const [expanded, setExpanded] = useState(false);
  const label = CLASS_LABELS[classification] || classification?.replace(/_/g, " ");
  const colorClass = CLASS_COLORS[classification] || CLASS_COLORS.other;
  const dupCount = pages.filter(p => p.is_duplicate).length;
  const uniquePages = pages.filter(p => !p.is_duplicate);

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50/70 transition-colors text-left"
        data-testid={`doc-group-${classification}`}
      >
        {expanded
          ? <CaretDown size={12} className="text-gray-400 flex-shrink-0" />
          : <CaretRight size={12} className="text-gray-400 flex-shrink-0" />
        }
        <Badge variant="outline" className={`text-xs flex-shrink-0 ${colorClass}`}>
          {label}
        </Badge>
        <span className="text-xs text-gray-500 flex-shrink-0">
          {pages.length} {pages.length === 1 ? "page" : "pages"}
        </span>
        {dupCount > 0 && (
          <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200 gap-0.5 flex-shrink-0">
            <Copy size={9} /> {dupCount} dup
          </Badge>
        )}
        {/* Show first unique page summary as preview */}
        {!expanded && uniquePages[0]?.summary && (
          <span className="text-xs text-gray-400 truncate ml-1">
            — {uniquePages[0].summary.slice(0, 80)}...
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-1.5">
          {pages.map(p => (
            <div
              key={p.page_number}
              onClick={() => onPageClick?.(p.page_number)}
              className={`rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                p.is_duplicate
                  ? "bg-red-50/50 border-red-100 hover:bg-red-50"
                  : "bg-white border-gray-100 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono font-semibold text-gray-500 w-7">P{p.page_number}</span>
                {p.is_duplicate && (
                  <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200 gap-0.5">
                    <Copy size={8} /> Dup of P{p.duplicate_of}
                  </Badge>
                )}
                {p.confidence > 0 && (
                  <span className="text-[10px] text-gray-400">{Math.round(p.confidence * 100)}%</span>
                )}
              </div>
              {p.summary && (
                <p className="text-xs text-gray-600 leading-relaxed pl-7">
                  {p.summary}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OverviewTab({ claim, onSelectPage, onUpdate }) {
  const patient = claim.patient_info || {};
  const policy = claim.policy_info || {};
  const pages = claim.pages || [];
  const isProcessed = claim.status === "processed";
  const duplicateCount = claim.duplicates?.length || 0;

  // Group pages by classification
  const grouped = {};
  pages.forEach(p => {
    const cls = p.classification || "other";
    if (!grouped[cls]) grouped[cls] = [];
    grouped[cls].push(p);
  });

  // Rule summary
  const ruleResults = claim.rule_results || [];
  const passedCount = ruleResults.filter(r => r.status === "passed").length;
  const warningCount = ruleResults.filter(r => r.status === "warning").length;
  const failedCount = ruleResults.filter(r => r.status === "failed").length;

  const formatAmount = (amt) => {
    if (!amt && amt !== 0) return "-";
    return `INR ${Number(amt).toLocaleString("en-IN")}`;
  };

  return (
    <div className="p-6 space-y-6" data-testid="overview-tab">
      {/* Status Banner */}
      {!isProcessed && claim.status !== "processing" && (
        <div className="border border-blue-200 bg-blue-50 rounded-md p-4">
          <p className="text-sm text-blue-800 font-medium">
            Click &ldquo;Process with AI&rdquo; to analyze this document and extract claim data.
          </p>
        </div>
      )}

      {claim.status === "processing" && (
        <div className="border border-yellow-200 bg-yellow-50 rounded-md p-4">
          <p className="text-sm text-yellow-800 font-medium">
            AI is processing your document...
          </p>
          <div className="mt-2 h-1.5 bg-yellow-200 rounded-full overflow-hidden">
            <div className="h-full bg-yellow-500 rounded-full animate-pulse w-2/3" />
          </div>
        </div>
      )}

      {/* Document Summary */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <FileText size={18} className="text-[#002FA7]" />
          Document Summary
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="border border-gray-200 rounded-md p-3">
            <p className="label-xs mb-1">Total Pages</p>
            <p className="text-2xl font-bold text-gray-900" data-testid="total-pages-count">{claim.total_pages}</p>
          </div>
          <div className="border border-gray-200 rounded-md p-3">
            <p className="label-xs mb-1">Unique Documents</p>
            <p className="text-2xl font-bold text-gray-900" data-testid="unique-docs-count">
              {claim.total_pages - duplicateCount}
            </p>
          </div>
          <div className="border border-gray-200 rounded-md p-3">
            <p className="label-xs mb-1">Duplicates</p>
            <p className={`text-2xl font-bold ${duplicateCount > 0 ? 'text-red-600' : 'text-gray-900'}`} data-testid="duplicate-count">
              {duplicateCount}
            </p>
          </div>
        </div>

        {/* Document Breakdown — expandable groups with per-page summaries */}
        {pages.length > 0 && (
          <div className="mt-4 border border-gray-200 rounded-md overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
              <p className="label-xs">Document Breakdown</p>
              <span className="text-[10px] text-gray-400">Click to expand and view page details</span>
            </div>
            <div>
              {Object.entries(grouped).map(([cls, pgs]) => (
                <DocGroup
                  key={cls}
                  classification={cls}
                  pages={pgs}
                  onPageClick={onSelectPage}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Patient Info */}
      {isProcessed && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <User size={18} className="text-[#002FA7]" />
            Patient Information
          </h3>
          <div className="grid grid-cols-2 gap-y-4 gap-x-6 border border-gray-200 rounded-md p-4">
            <KVItem label="Patient Name" value={patient.patient_name} />
            <KVItem label="Age / Gender" value={[patient.age, patient.gender].filter(Boolean).join(" / ")} />
            <KVItem label="Hospital" value={patient.hospital_name} />
            <KVItem label="Doctor" value={patient.doctor_name} />
            <KVItem label="Admission Date" value={patient.date_of_admission} />
            <KVItem label="Discharge Date" value={patient.date_of_discharge} />
            <KVItem label="Days of Stay" value={patient.days_of_stay} />
            <KVItem label="Primary Diagnosis" value={patient.diagnosis_primary} />
          </div>
        </section>
      )}

      {/* KYC Documents */}
      {isProcessed && patient.kyc_documents && patient.kyc_documents.length > 0 && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText size={18} className="text-[#002FA7]" />
            KYC Verification
          </h3>
          <div className="space-y-2">
            {patient.kyc_documents.map((kyc, idx) => (
              <div key={idx} className="border border-gray-200 rounded-md p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="text-xs capitalize bg-indigo-50 text-indigo-700 border-indigo-200">
                    {(kyc.document_type || "ID").replace(/_/g, " ")}
                  </Badge>
                  <span className="text-xs text-gray-400">Page {kyc.page}</span>
                </div>
                <div className="grid grid-cols-2 gap-y-2 gap-x-6">
                  <KVItem label="Full Name" value={kyc.full_name} />
                  <KVItem label="ID Number" value={kyc.id_number} />
                  <KVItem label="Date of Birth" value={kyc.date_of_birth} />
                  <KVItem label="Gender" value={kyc.gender} />
                  {kyc.father_name && <KVItem label="Father's Name" value={kyc.father_name} />}
                  {kyc.address && <KVItem label="Address" value={kyc.address} />}
                </div>
              </div>
            ))}
          </div>
          {/* Name verification across documents */}
          {patient.kyc_documents.length > 0 && patient.patient_name && (() => {
            // Proper name matching: strip titles, compare meaningful words
            const normalize = (n) => (n || "").toLowerCase().replace(/^(mr\.?|mrs\.?|ms\.?|dr\.?|shri\.?|smt\.?)\s*/i, "").replace(/[^a-z\s]/g, "").trim().split(/\s+/).filter(w => w.length > 1);
            const patientWords = normalize(patient.patient_name);
            const isMatch = patient.kyc_documents.some(k => {
              if (!k.full_name) return false;
              const kycWords = normalize(k.full_name);
              if (patientWords.length === 0 || kycWords.length === 0) return false;
              const matchCount = patientWords.filter(pw => kycWords.some(kw => kw === pw || kw.includes(pw) || pw.includes(kw))).length;
              return matchCount >= Math.min(2, patientWords.length);
            });
            return (
              <div className={`mt-2 border rounded-md p-2.5 text-xs flex items-center gap-2 ${
                isMatch ? "border-green-200 bg-green-50 text-green-800" : "border-yellow-200 bg-yellow-50 text-yellow-800"
              }`}>
                {isMatch
                  ? <><CheckCircle size={14} weight="fill" className="text-green-600" /> Name matched across documents</>
                  : <><Warning size={14} weight="fill" className="text-yellow-600" /> Patient name may differ from KYC — manual verification needed</>
                }
              </div>
            );
          })()}
        </section>
      )}

      {/* Bank Details */}
      {isProcessed && patient.bank_details && patient.bank_details.account_number && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText size={18} className="text-[#002FA7]" />
            Bank Details (Cancelled Cheque)
          </h3>
          <div className="grid grid-cols-2 gap-y-3 gap-x-6 border border-gray-200 rounded-md p-4">
            <KVItem label="Account Holder" value={patient.bank_details.account_holder} />
            <KVItem label="Bank Name" value={patient.bank_details.bank_name} />
            <KVItem label="Account Number" value={patient.bank_details.account_number} />
            <KVItem label="IFSC Code" value={patient.bank_details.ifsc_code} />
            <KVItem label="Branch" value={patient.bank_details.branch} />
          </div>
        </section>
      )}

      {/* Clinical Summary & Adjudication Controls */}
      {isProcessed && (
        <AdjudicationPanel claim={claim} onUpdate={onUpdate} />
      )}

      {/* Policy Info */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Heartbeat size={18} className="text-[#002FA7]" />
          Policy Information
        </h3>
        {Object.keys(policy).length > 0 ? (
          <div className="grid grid-cols-2 gap-y-4 gap-x-6 border border-gray-200 rounded-md p-4">
            <KVItem label="Sum Insured" value={formatAmount(policy.sum_insured)} />
            <KVItem label="Plan Type" value={policy.plan_type?.charAt(0).toUpperCase() + policy.plan_type?.slice(1)} />
            <KVItem label="Product" value={policy.product_name} />
            <KVItem label="Policy Number" value={policy.policy_number} />
            <KVItem label="Copay" value={policy.copay_percent ? `${policy.copay_percent}%` : null} />
            <KVItem label="Deductible" value={policy.deductible ? formatAmount(policy.deductible) : null} />
            <KVItem label="Network Hospital" value={policy.is_network_hospital === true ? "Yes" : policy.is_network_hospital === false ? "No" : null} />
            <KVItem label="Policy Period" value={policy.policy_start ? `${policy.policy_start} to ${policy.policy_end}` : null} />
            <KVItem label="Room Rent Limit" value={policy.room_rent_limit ? formatAmount(policy.room_rent_limit) + "/day" : null} />
            <KVItem label="Pre-Hosp Days" value={policy.pre_hosp_days} />
            <KVItem label="Post-Hosp Days" value={policy.post_hosp_days} />
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 rounded-md p-4 text-center bg-gray-50">
            <Heartbeat size={24} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500 font-medium">No policy uploaded yet</p>
            <p className="text-xs text-gray-400 mt-1">Upload a Policy PDF or JSON to apply policy-specific rules</p>
          </div>
        )}
      </section>

      {/* Reference Documents Links */}
      {isProcessed && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Link size={18} className="text-[#002FA7]" />
            Reference Documents
          </h3>
          <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
            <a
              href="https://customer-assets.emergentagent.com/job_pdf-claim-sorter/artifacts/payvyq7v_NME%20lIST-Updated%20%281%29.xlsx"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
              data-testid="nme-list-link"
            >
              <FileText size={16} className="text-red-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Non-Medical Expense (NME) List</p>
                <p className="text-xs text-gray-500">Aditya Birla Health Insurance — Non-payable items reference</p>
              </div>
              <span className="text-xs text-[#002FA7] flex-shrink-0 ml-auto">Open</span>
            </a>
          </div>
        </section>
      )}

      {/* Quick Rule Summary */}
      {isProcessed && ruleResults.length > 0 && (
        <section>
          <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <ClockCounterClockwise size={18} className="text-[#002FA7]" />
            Rule Engine Summary
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="border border-green-200 bg-green-50 rounded-md p-3 flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-green-600" />
              <div>
                <p className="text-xl font-bold text-green-800">{passedCount}</p>
                <p className="text-xs text-green-700">Passed</p>
              </div>
            </div>
            <div className="border border-yellow-200 bg-yellow-50 rounded-md p-3 flex items-center gap-2">
              <Warning size={20} weight="fill" className="text-yellow-600" />
              <div>
                <p className="text-xl font-bold text-yellow-800">{warningCount}</p>
                <p className="text-xs text-yellow-700">Warnings</p>
              </div>
            </div>
            <div className="border border-red-200 bg-red-50 rounded-md p-3 flex items-center gap-2">
              <XCircle size={20} weight="fill" className="text-red-600" />
              <div>
                <p className="text-xl font-bold text-red-800">{failedCount}</p>
                <p className="text-xs text-red-700">Failed</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Final Amount Quick View */}
      {isProcessed && claim.final_amount?.net_payable !== undefined && (
        <section>
          <div className="border-2 border-[#002FA7] rounded-md p-4 bg-blue-50/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-xs mb-1">Net Payable Amount</p>
                <p className="text-3xl font-bold text-[#002FA7]" data-testid="net-payable-overview">
                  {formatAmount(claim.final_amount.net_payable)}
                </p>
              </div>
              <div className="text-right">
                <p className="label-xs mb-1">Decision</p>
                <Badge
                  className={`text-sm px-3 py-1 ${
                    claim.final_amount.decision === "approved" ? "bg-green-100 text-green-800 border-green-300" :
                    claim.final_amount.decision === "rejected" ? "bg-red-100 text-red-800 border-red-300" :
                    "bg-yellow-100 text-yellow-800 border-yellow-300"
                  }`}
                  variant="outline"
                  data-testid="decision-badge"
                >
                  {claim.final_amount.decision?.replace(/_/g, " ").toUpperCase()}
                </Badge>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
